import { useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import {
    Badge,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    Button,
    Input,
    Text,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    Field,
    FieldGroup,
    FieldLabel,
    FieldSet,
    thumbnailGradients,
    Spinner,
    Textarea,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    Text as UiText,
} from "@athena/ui";
import { Check, Play, Plus, Search, Upload } from "lucide-react";
import { cn } from "@athena/ui/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useVideoUpload } from "@/hooks/use-video-upload";
import { SharedVideoAssetsDocument, VideoAssetsDocument, type VideoAssetsQuery } from "@/graphql/media.generated";
import { TagsDocument } from "@/graphql/tags.generated";
import { BrandsDocument } from "@/graphql/brands.generated";
import { formatDuration } from "../../../../global/formatHelpers";

type PickableVideoAsset = VideoAssetsQuery["videoAssets"][number];

export interface UploadedVideo {
    videoAssetId: string;
    title: string;
    description?: string;
    /**
     * Chosen here, written by the caller: tags hang off the `content` row (`setContentTags`), and
     * this dialog knows the video asset but not the content item that will present it.
     */
    tagIds: string[];
    /** Brand slugs, likewise a field on the content item's own update input. */
    brands: string[];
}

interface VideoSelectorModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /**
     * Whose videos to list, or undefined for a platform admin, who belongs to no organization and
     * sees every organization's.
     */
    organizationId?: string;
    /**
     * Where a video uploaded from this modal is owned, if that differs from the listing scope —
     * e.g. a course's own org, when `organizationId` above is the *viewer's* reach rather than the
     * course's. Defaults to `organizationId` when omitted. `isPlatformAdmin` decides whether an
     * absent one is refused or left for the server to pick, see `handleFile`.
     */
    uploadOrganizationId?: string;
    /**
     * Lets an upload proceed with no owner organization — the server falls back to this admin's
     * own org, or the shared library org if they have none. Anyone else still needs a concrete
     * owner organization to upload at all.
     */
    isPlatformAdmin?: boolean;
    onUpload: (video: UploadedVideo) => void;
}

const VideoThumbnail = ({ duration, index }: { duration: string; index: number }) => {
    const gradient = thumbnailGradients[index % thumbnailGradients.length];

    return (
        <div
            className={cn(
                "relative flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md bg-linear-to-br",
                gradient,
            )}
        >
            <div className="border-border rounded-full border-[0.1px] bg-white/20 p-2">
                <Play className="h-3 w-3 fill-white text-white opacity-80" />
            </div>
            <span className="absolute right-1.5 bottom-1.5 rounded bg-black/60 px-1 py-px font-mono text-[10px] font-medium text-white">
                {duration}
            </span>
        </div>
    );
};

const VideoAssetGrid = ({
    assets,
    selectedId,
    onSelect,
    emptyLabel,
}: {
    assets: PickableVideoAsset[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    emptyLabel: string;
}) => {
    const { t } = useTranslation();
    if (assets.length === 0) {
        return <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">{emptyLabel}</div>;
    }
    return (
        <div className="grid grid-cols-2 gap-2">
            {assets.map((asset, i) => {
                const selected = selectedId === asset.id;
                return (
                    <button
                        key={asset.id}
                        type="button"
                        onClick={() => onSelect(asset.id)}
                        className={cn(
                            "flex items-center gap-3 rounded-lg border p-3 text-left transition-colors",
                            selected ? "border-secondary/80 bg-secondary/10" : "border-border hover:bg-muted/50",
                        )}
                    >
                        <VideoThumbnail duration={formatDuration(asset.durationSeconds)} index={i} />
                        <div className="min-w-0 flex-1">
                            <Text size="sm" weight="medium" className="line-clamp-2 leading-snug">
                                {asset.title ?? asset.originalFilename ?? t("admin.lms.uploadNew")}
                            </Text>
                            <Badge variant={asset.status === "READY" ? "default" : "secondary"}>{asset.status}</Badge>
                        </div>
                        <div
                            className={cn(
                                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                                selected ? "border-secondary bg-secondary text-white" : "border-muted-foreground/30",
                            )}
                        >
                            {selected && <Check className="h-3 w-3 stroke-3 text-white" />}
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

export function VideoSelectorModal({
    open,
    onOpenChange,
    organizationId,
    uploadOrganizationId,
    isPlatformAdmin,
    onUpload,
}: VideoSelectorModalProps) {
    const { t } = useTranslation();
    const [
        selectedUploadId,
        setSelectedUploadId,
    ] = useState<string | null>(null);
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        activeTab,
        setActiveTab,
    ] = useState("uploads");
    const [
        newVideoTitle,
        setNewVideoTitle,
    ] = useState<string>("");
    const [
        newVideoDescription,
        setNewVideoDescription,
    ] = useState<string>("");
    const [
        selectedTagIds,
        setSelectedTagIds,
    ] = useState<Set<string>>(new Set());
    const [
        selectedBrands,
        setSelectedBrands,
    ] = useState<Set<string>>(new Set());
    const [
        tagsOpen,
        setTagsOpen,
    ] = useState<boolean>(false);
    const [
        brandsOpen,
        setBrandsOpen,
    ] = useState<boolean>(false);
    /** Open only while an upload is in flight and the operator has tried to leave. */
    const [
        confirmingCancel,
        setConfirmingCancel,
    ] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { upload, cancel, uploading, progress, error } = useVideoUpload();
    const { data: uploadsData, loading: uploadsLoading } = useQuery(VideoAssetsDocument, {
        variables: { organizationId },
        skip: activeTab !== "uploads",
    });
    const uploads = (uploadsData?.videoAssets ?? []).filter((a) => a.status !== "FAILED");
    const filteredUploads = uploads.filter((a) =>
        (a.originalFilename ?? "").toLowerCase().includes(search.toLowerCase()),
    );

    // "Shared" means this org's parent and sub-org uploads, so it has no meaning without an org.
    // A platform admin already sees every asset under Your uploads; skipping avoids sending a
    // query whose required argument we do not have.
    const { data: sharedData, loading: sharedLoading } = useQuery(SharedVideoAssetsDocument, {
        variables: { organizationId: organizationId ?? "" },
        skip: activeTab !== "library" || !organizationId,
    });
    const sharedVideos = (sharedData?.sharedVideoAssets ?? []).filter((a) => a.status !== "FAILED");
    const filteredShared = sharedVideos.filter((a) =>
        (a.originalFilename ?? "").toLowerCase().includes(search.toLowerCase()),
    );

    /*
     * The two pickers' options. Only fetched while the upload tab is open — the other two tabs
     * pick an existing video and describe nothing.
     */
    const pickersSkipped = activeTab !== "upload-new";
    const { data: tagsData } = useQuery(TagsDocument, { skip: pickersSkipped });
    const { data: brandsData } = useQuery(BrandsDocument, { skip: pickersSkipped });
    const allTags = tagsData?.tags ?? [];
    /*
     * Release U is dropped the same way ContentEditor drops it: a brand that sees everything is
     * never stored, so offering it would let an operator "select" something with no effect.
     */
    const allBrands = (brandsData?.brands ?? []).filter((b) => !b.seesAllContent);

    const ownerOrganizationId = uploadOrganizationId ?? organizationId;

    async function handleFile(file: File) {
        try {
            // An upload has to belong somewhere. A platform admin viewing every organization
            // hasn't said which, but the server already knows how to place one of theirs — their
            // own org, or the shared library org if they have none. Anyone else has to say.
            if (!ownerOrganizationId && !isPlatformAdmin) {
                toast.error("Open this from an organization to upload a video — a new video needs an owner.");
                return;
            }
            const { assetId } = await upload(file, ownerOrganizationId, newVideoTitle.trim());
            onUpload({
                videoAssetId: assetId,
                title: newVideoTitle.trim() || file.name,
                description: newVideoDescription.trim() || undefined,
                tagIds: Array.from(selectedTagIds),
                brands: Array.from(selectedBrands),
            });
            onOpenChange(false);
            setNewVideoTitle("");
            setNewVideoDescription("");
            setSelectedTagIds(new Set());
            setSelectedBrands(new Set());
        } catch {
            // upload() already records the error for display below.
        }
    }

    const handleAddUpload = () => {
        const asset =
            uploads.find((a) => a.id === selectedUploadId) ?? sharedVideos.find((a) => a.id === selectedUploadId);
        if (asset) {
            onUpload({
                videoAssetId: asset.id,
                title: asset.title ?? asset.originalFilename ?? t("admin.lms.uploadNew"),
                // Nothing was described on this tab — picking an existing video says only which one.
                tagIds: [],
                brands: [],
            });
            onOpenChange(false);
            setSelectedUploadId(null);
        }
    };

    const toggleTag = (tagId: string) =>
        setSelectedTagIds((prev) => {
            const next = new Set(prev);
            next.has(tagId) ? next.delete(tagId) : next.add(tagId);
            return next;
        });

    const toggleBrand = (slug: string) =>
        setSelectedBrands((prev) => {
            const next = new Set(prev);
            next.has(slug) ? next.delete(slug) : next.add(slug);
            return next;
        });

    const handleCancel = () => {
        requestClose();
        setSelectedUploadId(null);
        setSearch("");
    };

    /**
     * Every way out of this dialog — the X, Escape, clicking the overlay, the Cancel button —
     * routes through here, so mid-upload they all ask first.
     *
     * They used to close immediately, which did *not* stop the upload: unmounting a React tree
     * does not abort a `fetch`, so the remaining parts kept uploading and `completeVideoUpload`
     * fired afterwards, finishing an upload with nothing left on screen to show it. Leaving now
     * cancels it for real.
     */
    const requestClose = () => {
        if (uploading) setConfirmingCancel(true);
        else onOpenChange(false);
    };

    const confirmCancel = () => {
        cancel();
        setConfirmingCancel(false);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}>
            <DialogContent className="flex max-h-[80vh] w-full max-w-4xl flex-col gap-0 overflow-hidden p-0">
                <div className="border-border border-b px-6 pt-6 pb-4">
                    <DialogHeader>
                        <DialogTitle className="font-display text-lg font-semibold">
                            {t("admin.lms.addVideo")}
                        </DialogTitle>
                        <DialogDescription className="text-muted-foreground text-sm">
                            {t("admin.lms.addVideoSubtitle")}
                        </DialogDescription>
                    </DialogHeader>
                </div>
                <Tabs
                    defaultValue="library"
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="flex min-h-0 flex-1 flex-col overflow-hidden"
                >
                    <div className="border-border flex items-center justify-between border-b px-6 py-3">
                        <TabsList className="bg-muted h-auto gap-0 rounded-lg p-1">
                            <TabsTrigger
                                value="uploads"
                                className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground rounded-md px-3 py-1.5 text-sm transition-colors data-[state=active]:shadow-sm"
                            >
                                {t("admin.lms.yourUploads")}
                            </TabsTrigger>
                            <TabsTrigger
                                value="library"
                                className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground rounded-md px-3 py-1.5 text-sm transition-colors data-[state=active]:shadow-sm"
                            >
                                {t("admin.lms.sharedLibrary")}
                            </TabsTrigger>
                            <TabsTrigger
                                value="upload-new"
                                className="data-[state=active]:bg-background data-[state=active]:text-foreground text-muted-foreground flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors data-[state=active]:shadow-sm"
                            >
                                <Upload className="h-3 w-3" />
                                {t("admin.lms.uploadNew")}
                            </TabsTrigger>
                        </TabsList>
                        {activeTab !== "upload-new" && (
                            <div className="relative">
                                <Search className="text-muted-foreground absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" />
                                <Input
                                    className="h-8 w-65 pl-8 text-xs"
                                    placeholder={t("admin.lms.searchByTitleOrPresenter")}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        )}
                    </div>

                    <TabsContent value="library" className="mt-0 flex-1 overflow-y-auto p-4">
                        {sharedLoading ? (
                            <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">
                                {t("common.states.loading")}
                            </div>
                        ) : (
                            <VideoAssetGrid
                                assets={filteredShared}
                                selectedId={selectedUploadId}
                                onSelect={(id) => setSelectedUploadId(selectedUploadId === id ? null : id)}
                                // "Shared" means a parent org's and sub-orgs' uploads, so it has no
                                // meaning without an org and the query above is skipped. Saying "no
                                // uploads yet" there reads as an empty library to the one caller who
                                // is actually seeing every upload on the other tab.
                                emptyLabel={
                                    organizationId
                                        ? t("admin.lms.noUploadsYet")
                                        : "Sharing follows an organization's parent and sub-organizations. Your uploads already lists every video."
                                }
                            />
                        )}
                    </TabsContent>
                    <TabsContent value="uploads" className="mt-0 flex-1 overflow-y-auto p-4">
                        {uploadsLoading ? (
                            <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">
                                {t("common.states.loading")}
                            </div>
                        ) : (
                            <VideoAssetGrid
                                assets={filteredUploads}
                                selectedId={selectedUploadId}
                                onSelect={(id) => setSelectedUploadId(selectedUploadId === id ? null : id)}
                                emptyLabel={t("admin.lms.noUploadsYet")}
                            />
                        )}
                    </TabsContent>

                    <TabsContent value="upload-new" className="mt-0 flex-1 overflow-y-auto p-4">
                        <FieldSet className="mb-4 w-full max-w-none">
                            <FieldGroup>
                                <div className="grid-row-2 grid gap-4">
                                    <Field>
                                        <FieldLabel htmlFor="title" required>
                                            {t("common.fields.title")}
                                        </FieldLabel>
                                        <Input
                                            required
                                            id="title"
                                            placeholder="Introduction to Potions"
                                            value={newVideoTitle}
                                            onChange={(e) => setNewVideoTitle(e.target.value)}
                                        />
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="description">{t("common.fields.description")}</FieldLabel>
                                        <Textarea
                                            id="description"
                                            placeholder="In this video, we are going to learn the basics of potion making..."
                                            value={newVideoDescription}
                                            onChange={(e) => setNewVideoDescription(e.target.value)}
                                        />
                                    </Field>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="video/*"
                                        aria-label={t("admin.lms.dragFileOrBrowse")}
                                        className="sr-only"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) void handleFile(file);
                                        }}
                                        required
                                    />
                                    <button
                                        type="button"
                                        disabled={uploading || !newVideoTitle.trim()}
                                        onClick={() => fileInputRef.current?.click()}
                                        className="border-border hover:bg-muted/40 flex h-32 w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {uploading ? <Spinner /> : <Upload className="text-muted-foreground h-6 w-6" />}
                                        <Text size="sm" muted>
                                            {uploading
                                                ? `${t("admin.lms.uploading")} ${Math.round(progress * 100)}%`
                                                : newVideoTitle.trim()
                                                  ? t("admin.lms.dragFileOrBrowse")
                                                  : t("admin.lms.enterTitleFirst")}
                                        </Text>
                                    </button>
                                    <Field>
                                        <FieldLabel htmlFor="video-tags">{t("common.fields.tags")}</FieldLabel>
                                        <div className="flex flex-col gap-2">
                                            {selectedTagIds.size > 0 && (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {allTags
                                                        .filter((tag) => selectedTagIds.has(tag.id))
                                                        .map((tag) => (
                                                            <button
                                                                key={tag.id}
                                                                type="button"
                                                                aria-label={`Remove ${tag.name}`}
                                                                onClick={() => toggleTag(tag.id)}
                                                                className="border-border bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                                                            >
                                                                {tag.name}
                                                                <span className="text-xs">×</span>
                                                            </button>
                                                        ))}
                                                </div>
                                            )}
                                            <DropdownMenu open={tagsOpen} onOpenChange={setTagsOpen}>
                                                <DropdownMenuTrigger asChild>
                                                    <Badge
                                                        id="video-tags"
                                                        variant={"dottedOutline"}
                                                        className={cn(
                                                            tagsOpen && "border-secondary border-dashed",
                                                            "w-fit",
                                                        )}
                                                    >
                                                        <Plus className="mr-1 h-3.5 w-3.5" /> {t("admin.lms.addTag")}
                                                    </Badge>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent className="bg-card w-48" align="start">
                                                    <DropdownMenuGroup>
                                                        {allTags.map((tag) => (
                                                            <DropdownMenuItem
                                                                key={tag.id}
                                                                onSelect={(e) => {
                                                                    e.preventDefault();
                                                                    toggleTag(tag.id);
                                                                }}
                                                                className="flex items-center justify-between"
                                                            >
                                                                {tag.name}
                                                                {selectedTagIds.has(tag.id) && (
                                                                    <Check className="h-3.5 w-3.5" />
                                                                )}
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuGroup>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="video-brands">{t("common.fields.brands")}</FieldLabel>
                                        <UiText size="sm" muted>
                                            {t("admin.lms.brandDescription")}
                                        </UiText>
                                        <div className="flex flex-col gap-2">
                                            {selectedBrands.size > 0 && (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {allBrands
                                                        .filter((brand) => selectedBrands.has(brand.slug))
                                                        .map((brand) => (
                                                            <button
                                                                key={brand.id}
                                                                type="button"
                                                                aria-label={`Remove ${brand.name}`}
                                                                onClick={() => toggleBrand(brand.slug)}
                                                                className="border-border bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                                                            >
                                                                {brand.name}
                                                                <span className="text-xs">×</span>
                                                            </button>
                                                        ))}
                                                </div>
                                            )}
                                            <DropdownMenu open={brandsOpen} onOpenChange={setBrandsOpen}>
                                                <DropdownMenuTrigger asChild>
                                                    <Badge
                                                        id="video-brands"
                                                        variant={"dottedOutline"}
                                                        className={cn(
                                                            brandsOpen && "border-secondary border-dashed",
                                                            "w-fit",
                                                        )}
                                                    >
                                                        <Plus className="mr-1 h-3.5 w-3.5" /> {t("admin.lms.addBrand")}
                                                    </Badge>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent className="bg-card w-48" align="start">
                                                    <DropdownMenuGroup>
                                                        {allBrands.map((brand) => (
                                                            <DropdownMenuItem
                                                                key={brand.id}
                                                                onSelect={(e) => {
                                                                    e.preventDefault();
                                                                    toggleBrand(brand.slug);
                                                                }}
                                                                className="flex items-center justify-between"
                                                            >
                                                                {brand.name}
                                                                {selectedBrands.has(brand.slug) && (
                                                                    <Check className="h-3.5 w-3.5" />
                                                                )}
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuGroup>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </Field>
                                </div>
                            </FieldGroup>
                        </FieldSet>
                        {error && (
                            <Text className="text-destructive mt-2 text-sm" role="alert">
                                {error}
                            </Text>
                        )}
                    </TabsContent>
                </Tabs>
                {activeTab !== "upload-new" && (
                    <div className="border-border flex items-center justify-between border-t px-6 py-4">
                        <Text size="sm" muted>
                            {activeTab === "uploads" && t("admin.lms.videoCount", { count: filteredUploads.length })}
                            {activeTab === "library" && t("admin.lms.videoCount", { count: filteredShared.length })}
                        </Text>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={handleCancel}>
                                {t("common.actions.cancel")}
                            </Button>
                            <Button
                                variant={"secondary"}
                                size="sm"
                                disabled={!selectedUploadId}
                                onClick={handleAddUpload}
                            >
                                {t("common.actions.addVariable", { variable: "Video" })}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>

            {/*
             * Not dismissible by Escape or the overlay: the whole point is that leaving is what
             * destroys the upload, so the way out has to be a deliberate answer to the question.
             */}
            <Dialog open={confirmingCancel} onOpenChange={(next) => !next && setConfirmingCancel(false)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Cancel this upload?</DialogTitle>
                        <DialogDescription>
                            {Math.round(progress * 100)}% of this video has transferred. Closing now stops it, and
                            nothing is kept — you would have to start the upload again.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-end gap-2">
                        {/*
                         * Closes this dialog and the one behind it, not just this one — the upload
                         * itself lives in this component's own state, not the Dialog subtree, so it
                         * keeps running unattended. `confirmCancel` is the only path that stops it.
                         */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setConfirmingCancel(false);
                                onOpenChange(false);
                            }}
                        >
                            Keep uploading
                        </Button>
                        {/* Repeats the action rather than saying OK, per `.claude/rules/ui.md`. */}
                        <Button variant="destructive" size="sm" onClick={confirmCancel}>
                            Cancel upload
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}
